import { useCallback, useEffect } from 'react';

import { CustomerLedgerUpdateTypeEnum, useDynamicCallback, useGlobalToasts } from '@talos/kyoko';

import type { CustomerBalanceTransactionDrawerState } from '../types';
import { createVerifyPastedValueIsNumber } from '../utils';

interface UseFormHandlersProps {
  drawerState: CustomerBalanceTransactionDrawerState;
  updateDrawerState: (updates: Partial<CustomerBalanceTransactionDrawerState>) => void;
  customerMarketAccounts: string[];
}

export function useFormHandlers({ drawerState, updateDrawerState, customerMarketAccounts }: UseFormHandlersProps) {
  const { add: addToast } = useGlobalToasts();

  const handleCustomerChange = useDynamicCallback((customer?: string) => {
    updateDrawerState({
      customer,
      // Reset account, source and destination when customer account changes
      customerAccount: undefined,
      source: undefined,
      destination: undefined,
    });
  });

  const handleCustomerAccountChange = useDynamicCallback((customerAccount?: string) => {
    updateDrawerState({
      customerAccount,
      // Reset source and destination when customer account changes
      source: undefined,
      destination: undefined,
    });
  });

  const handleTransferTypeChange = useDynamicCallback((transferType: CustomerLedgerUpdateTypeEnum) => {
    updateDrawerState({ transferType });
    // Clear txHash when switching to initiate method
    if (drawerState.transferMethod === 'initiate') {
      updateDrawerState({ txHash: '' });
    }
  });

  const handleTransferMethodChange = useDynamicCallback((transferMethod: 'initiate' | 'manual') => {
    updateDrawerState({ transferMethod });
    // Clear txHash when switching to initiate withdrawal (deposits can still have txHash)
    if (transferMethod === 'initiate' && drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw) {
      updateDrawerState({ txHash: '' });
    }
  });

  // Helper functions
  const verifyPastedValueIsNumber = useCallback(() => createVerifyPastedValueIsNumber(addToast), [addToast]);

  // If only 1 customer account, set it automatically
  useEffect(() => {
    if (customerMarketAccounts.length === 1) {
      updateDrawerState({ customerAccount: customerMarketAccounts[0] });
    }
  }, [customerMarketAccounts, updateDrawerState]);

  return {
    handleCustomerChange,
    handleCustomerAccountChange,
    handleTransferTypeChange,
    handleTransferMethodChange,
    verifyPastedValueIsNumber,
  };
}
